import { useState, useEffect } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PredictiveInsightsBanner } from '@/components/PredictiveInsightsBanner';

/**
 * Dashboard section titles (API stores backend category on each metric; we group here).
 * Backend: Account Suspension Risk, Shipping Compliance, Catalogue Integrity, Buyer Trust, Operational Control
 */
const METRIC_SECTIONS = [
  { id: 'account-health', title: 'Account Health & Compliance', subtitle: 'Account Suspension Risk' },
  { id: 'shipping', title: 'Shipping & Fulfillment', subtitle: 'Shipping Compliance' },
  { id: 'catalog', title: 'Catalog & Listing Integrity', subtitle: 'Catalogue Integrity' },
  { id: 'buyer-trust', title: 'Customer Experience & Feedback', subtitle: 'Buyer Trust' },
  { id: 'security', title: 'Security & Access Monitoring', subtitle: 'Permissions, logins, API' },
  { id: 'fba', title: 'FBA Inventory Management', subtitle: 'IPI, stranded, stockouts' },
  { id: 'operations', title: 'Operations & Pricing', subtitle: 'Bulk pricing & other ops' },
];

const SECURITY_API_GROUPS = new Set(['permission_changes', 'login_geo_drift', 'api_activity_spike']);
const FBA_API_GROUPS = new Set(['ipi_score', 'stranded_inventory', 'inventory_stockout']);

/** Map API metric row → section id from METRIC_SECTIONS */
export function metricSectionId(metric) {
  const c = metric.category;
  const a = metric.api_group;
  if (c === 'Account Suspension Risk') return 'account-health';
  if (c === 'Shipping Compliance') return 'shipping';
  if (c === 'Catalogue Integrity') return 'catalog';
  if (c === 'Buyer Trust') return 'buyer-trust';
  if (c === 'Operational Control') {
    if (SECURITY_API_GROUPS.has(a)) return 'security';
    if (FBA_API_GROUPS.has(a)) return 'fba';
    return 'operations';
  }
  return 'other';
}

function getStatusStyles(status) {
  if (status === 'breached') return { bg: 'bg-[hsl(0,84%,60%)]/15', text: 'text-[hsl(0,84%,60%)]', border: 'border-[hsl(0,84%,60%)]/30', icon: 'Breached' };
  if (status === 'warning') return { bg: 'bg-[hsl(45,93%,58%)]/15', text: 'text-[hsl(45,93%,58%)]', border: 'border-[hsl(45,93%,58%)]/30', icon: 'Warning' };
  return { bg: 'bg-[hsl(142,72%,45%)]/15', text: 'text-[hsl(142,72%,45%)]', border: 'border-[hsl(142,72%,45%)]/30', icon: 'Safe' };
}

function getRiskBadge(level) {
  if (level === 'Critical') return 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30';
  if (level === 'High') return 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30';
  if (level === 'Medium') return 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30';
  return 'bg-muted/30 text-muted-foreground border-border/60';
}

function normalizeRealtimeStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'critical') return 'breached';
  if (s === 'warning') return 'warning';
  return null;
}

function applyRealtimeOverrides(metrics, overrides) {
  if (!Array.isArray(metrics) || metrics.length === 0) return [];
  if (!Array.isArray(overrides) || overrides.length === 0) return metrics;

  const byKey = new Map(overrides.map((o) => [o.key, o]));
  return metrics.map((metric) => {
    const override = byKey.get(metric.api_group);
    if (!override) return metric;

    const nextStatus = normalizeRealtimeStatus(override.status);
    return {
      ...metric,
      status: nextStatus || metric.status,
      realtime_override: {
        key: override.key,
        label: override.label,
        severity: override.severity,
        status: override.status,
        value: override.value,
        unit: override.unit,
        detail: override.detail,
      },
    };
  });
}

export default function MetricsPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing, dateFrom, dateTo } = useSeller();
  const [metrics, setMetrics] = useState([]);
  const [realtimePenalty, setRealtimePenalty] = useState(0);
  const [lastRealtimeUpdate, setLastRealtimeUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState(
    METRIC_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), { other: true })
  );
  const [selectedMetric, setSelectedMetric] = useState(null);
  const buildRealtimeParams = () => ({ realtime: 1, realtime_hours: 24, _ts: Date.now() });

  useEffect(() => {
    if (!currentSellerId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const [metricsRes, overviewRes] = await Promise.all([
          sellersAPI.getMetrics(currentSellerId, { dateFrom, dateTo }),
          sellersAPI.getOverview(currentSellerId, buildRealtimeParams()),
        ]);
        const overrides = Array.isArray(overviewRes.data?.realtime_kpi_overrides) ? overviewRes.data.realtime_kpi_overrides : [];
        setRealtimePenalty(Number(overviewRes.data?.realtime_overlay?.penalty || 0));
        setLastRealtimeUpdate(overviewRes.data?.realtime_overlay?.to || new Date().toISOString());
        setMetrics(applyRealtimeOverrides(metricsRes.data, overrides));
      } catch (err) {
        console.error('Failed to load metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentSellerId, syncVersion, dateFrom, dateTo]);

  useEffect(() => {
    if (!currentSellerId) return;
    const id = setInterval(async () => {
      try {
        const [metricsRes, overviewRes] = await Promise.all([
          sellersAPI.getMetrics(currentSellerId, { dateFrom, dateTo }),
          sellersAPI.getOverview(currentSellerId, buildRealtimeParams()),
        ]);
        const overrides = Array.isArray(overviewRes.data?.realtime_kpi_overrides) ? overviewRes.data.realtime_kpi_overrides : [];
        setRealtimePenalty(Number(overviewRes.data?.realtime_overlay?.penalty || 0));
        setLastRealtimeUpdate(overviewRes.data?.realtime_overlay?.to || new Date().toISOString());
        setMetrics(applyRealtimeOverrides(metricsRes.data, overrides));
      } catch (_) {
        // keep quiet; next tick retries
      }
    }, 30000);
    return () => clearInterval(id);
  }, [currentSellerId, syncVersion, dateFrom, dateTo]);

  const grouped = METRIC_SECTIONS.reduce((acc, s) => {
    acc[s.id] = metrics.filter(m => metricSectionId(m) === s.id);
    return acc;
  }, {});
  grouped.other = metrics.filter(m => metricSectionId(m) === 'other');
  const sectionOrder = [...METRIC_SECTIONS.map(s => s.id), ...(grouped.other.length ? ['other'] : [])];

  if (loading || (hasSelection && syncing)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="metrics-page">
      <div>
        <h1 className="text-xl font-semibold">Metrics Dashboard</h1>
        <p className="text-sm text-muted-foreground">All metrics grouped by domain (aligned with KPI categories)</p>
      </div>
      {realtimePenalty > 0 && (
        <div className="text-xs text-[hsl(24,94%,56%)] border border-[hsl(24,94%,56%)]/30 bg-[hsl(24,94%,56%)]/10 rounded-lg px-3 py-2">
          Live notification impact active: temporary score penalty {realtimePenalty} pts (updates every 30s).
        </div>
      )}
      {lastRealtimeUpdate && (
        <p className="text-xs text-muted-foreground">
          Last realtime update: {new Date(lastRealtimeUpdate).toLocaleString()}
        </p>
      )}

      {metrics.length === 0 && (
        <p className="text-sm text-muted-foreground border border-border/60 rounded-lg p-6 bg-muted/20">
          No metrics yet. Data appears after the account sync completes (sync runs automatically when you select an account and profile).
        </p>
      )}

      {sectionOrder.map((sectionId) => {
        const sectionMeta = METRIC_SECTIONS.find(s => s.id === sectionId);
        const title = sectionMeta?.title ?? 'Other';
        const subtitle = sectionId === 'other' ? 'Unmapped backend category' : sectionMeta?.subtitle;
        const list = grouped[sectionId] ?? [];
        if (list.length === 0) return null;
        return (
        <Collapsible
          key={sectionId}
          open={openCategories[sectionId]}
          onOpenChange={(open) => setOpenCategories(prev => ({ ...prev, [sectionId]: open }))}
        >
          <Card className="bg-card border-border/80">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="flex flex-row items-center justify-between cursor-pointer hover:bg-muted/20 transition-colors rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <CardTitle className="text-base font-semibold">{title}</CardTitle>
                    {subtitle && <p className="text-[11px] text-muted-foreground font-normal mt-0.5">{subtitle}</p>}
                  </div>
                  <Badge variant="outline" className="text-xs">{list.length} metrics</Badge>
                </div>
                {openCategories[sectionId] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {list.map((metric) => {
                    const styles = getStatusStyles(metric.status);
                    return (
                      <div
                        key={metric.id}
                        onClick={() => setSelectedMetric(metric)}
                        className="p-4 rounded-lg border border-border/60 bg-background/40 hover:bg-muted/20 hover:border-border cursor-pointer transition-colors"
                        data-testid={`metric-card-${metric.name.toLowerCase().replace(/[\s()]+/g, '-')}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium leading-tight">{metric.name}</h4>
                          <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 ml-2" />
                        </div>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-lg font-semibold tabular-nums">{metric.current_value}</span>
                          <span className="text-xs text-muted-foreground">/ {metric.threshold}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 border', styles.bg, styles.text, styles.border)}>
                            {styles.icon}
                          </Badge>
                          {metric.realtime_override && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] px-1.5 py-0 border',
                                String(metric.realtime_override.severity).toLowerCase() === 'critical'
                                  ? 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30'
                                  : 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30'
                              )}
                              title={metric.realtime_override.detail}
                            >
                              Live
                            </Badge>
                          )}
                          {metric.risk_level !== 'N/A' && (
                            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 border', getRiskBadge(metric.risk_level))}>
                              {metric.risk_level}
                            </Badge>
                          )}
                        </div>
                        {metric.realtime_override?.detail && (
                          <p className="mt-2 text-[11px] text-muted-foreground line-clamp-2">
                            {metric.realtime_override.detail}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
        );
      })}

      {/* Metric Detail Dialog */}
      <Dialog open={!!selectedMetric} onOpenChange={() => setSelectedMetric(null)}>
        <DialogContent className="bg-card border-border/80 max-w-lg" data-testid="metric-detail-dialog">
          {selectedMetric && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedMetric.name}</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {selectedMetric.category} | {selectedMetric.api_group}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                    <p className="text-lg font-semibold tabular-nums">{selectedMetric.current_value}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Threshold</p>
                    <p className="text-lg font-semibold tabular-nums">{selectedMetric.threshold}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(() => { const s = getStatusStyles(selectedMetric.status); return (
                    <Badge variant="outline" className={cn('border', s.bg, s.text, s.border)}>{s.icon}</Badge>
                  ); })()}
                  {selectedMetric.risk_level !== 'N/A' && (
                    <Badge variant="outline" className={cn('border', getRiskBadge(selectedMetric.risk_level))}>{selectedMetric.risk_level}</Badge>
                  )}
                  {selectedMetric.penalty > 0 && (
                    <Badge variant="outline" className="border-border/60">Penalty: {selectedMetric.penalty} pts</Badge>
                  )}
                  {selectedMetric.realtime_override && (
                    <Badge
                      variant="outline"
                      className={cn(
                        'border',
                        String(selectedMetric.realtime_override.severity).toLowerCase() === 'critical'
                          ? 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30'
                          : 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30'
                      )}
                    >
                      Live notification impact
                    </Badge>
                  )}
                </div>
                {selectedMetric.realtime_override?.detail && (
                  <div className="p-3 rounded-lg border border-[hsl(24,94%,56%)]/30 bg-[hsl(24,94%,56%)]/10">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Realtime update</p>
                    <p className="text-sm">{selectedMetric.realtime_override.detail}</p>
                  </div>
                )}
                {Array.isArray(selectedMetric.predictive_insights) && selectedMetric.predictive_insights.length > 0 && (
                  <PredictiveInsightsBanner insights={selectedMetric.predictive_insights} />
                )}
                {selectedMetric.api_group === 'policy_warnings' && Array.isArray(selectedMetric.scraped_evidence) && selectedMetric.scraped_evidence.length > 0 && (
                  <div className="p-4 rounded-lg bg-muted/20 border border-border/60">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Scraped Policy Evidence (ASIN + Reason)</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {selectedMetric.scraped_evidence.map((item, idx) => (
                        <div key={`${item.asin || 'na'}-${idx}`} className="text-sm border-b border-border/40 pb-2 last:border-0">
                          <p><span className="text-muted-foreground">ASIN:</span> {item.asin || 'N/A'}</p>
                          <p><span className="text-muted-foreground">Reason:</span> {item.reason || 'Policy warning'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-4 rounded-lg bg-muted/20 border border-border/60">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Corrective SOP</p>
                  <p className="text-sm">{selectedMetric.sop}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
